/*
 * B1 + B5 — the company's own pages, from one place.
 *
 * Team, Departments and Company Context used to be two different layouts. The
 * consultant page was a row of buttons where Team and Departments expanded in
 * place over an embedded copy of My Business; the client's Settings page had a
 * Members tab, a Departments tab, and no Company Context at all. Same three
 * things, two shapes, and one of them incomplete.
 *
 * This module owns the three panels. Two chromes consume them:
 *   - `CompanyTabs` draws its own tab bar (the consultant client page, /team)
 *   - `CompanyTabPanel` renders one panel inside a tab bar someone else owns
 *     (the Settings page, which already has grouped navigation)
 *
 * Roles are handled here rather than by forking the component. Problem scoping
 * is staff-only: a consultant or super sees it inside Company Context, a normal
 * admin never does.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Users, Building2, FileText } from 'lucide-react';

import ClientTeamTab from '../clients/ClientTeamTab';
import DepartmentsTab from '../settings/DepartmentsTab';
import MyBusinessHome from '../../pages/MyBusinessHome';

export type CompanyTabId = 'team' | 'departments' | 'context';

export interface CompanyTabDef {
  id: CompanyTabId;
  label: string;
  icon: React.ElementType;
}

export const COMPANY_TAB_DEFS: CompanyTabDef[] = [
  { id: 'team', label: 'Team', icon: Users },
  { id: 'departments', label: 'Departments', icon: Building2 },
  { id: 'context', label: 'Company Context', icon: FileText },
];

/** Members was the old name for the same tab, and older links still carry it. */
export const COMPANY_TAB_ALIASES: Record<string, CompanyTabId> = {
  members: 'team',
  dossier: 'context',
  brief: 'context',
};

export function normaliseCompanyTab(raw: string | null | undefined): CompanyTabId | null {
  const slug = (raw || '').toLowerCase();
  if (!slug) return null;
  const mapped = COMPANY_TAB_ALIASES[slug] ?? slug;
  return COMPANY_TAB_DEFS.some(t => t.id === mapped) ? (mapped as CompanyTabId) : null;
}

/**
 * Whether this viewer sees how the work is being scoped.
 *
 * Staff only. The client's own admin owns the company picture but not the
 * engagement's scoping state, and reading the role here keeps that rule in one
 * place instead of at each mount.
 */
export function canSeeProblemScoping(role?: string | null): boolean {
  // Mirrors the backend's own staff test (`user.role not in ('consultant',
  // 'super')`), so the tab and the API agree on who counts as staff.
  const r = (role ?? localStorage.getItem('user_role') ?? '').toLowerCase();
  return r === 'consultant' || r === 'super';
}

export interface CompanyTabPanelProps {
  tab: CompanyTabId;
  clientId: string;
  clientName: string;
  /** Staff viewing a client: the context panel reads rather than drives Orbit. */
  readOnlyContext?: boolean;
  /** Override the role check, for a caller that already knows. */
  role?: string | null;
}

export const CompanyTabPanel: React.FC<CompanyTabPanelProps> = ({
  tab, clientId, clientName, readOnlyContext = false, role,
}) => {
  if (!clientId) {
    return (
      <div className="rounded-xl border border-gray-100 bg-white px-5 py-12 text-center text-sm text-gray-500">
        Sign in again to manage your company.
      </div>
    );
  }
  if (tab === 'team') {
    return <ClientTeamTab clientId={clientId} clientName={clientName} />;
  }
  if (tab === 'departments') {
    return <DepartmentsTab clientId={clientId} />;
  }
  return (
    <MyBusinessHome
      clientId={clientId}
      readOnly={readOnlyContext}
      showScoping={canSeeProblemScoping(role)}
    />
  );
};

export interface CompanyTabsProps {
  clientId: string;
  clientName: string;
  initialTab?: CompanyTabId;
  /** Keep the open tab in `?tab=` so a tab is linkable and survives a reload. */
  urlSync?: boolean;
  readOnlyContext?: boolean;
  role?: string | null;
  /** Which tabs to offer. Departments and Company Context are manager work, so
   *  a caller that knows the viewer cannot manage the workspace narrows this
   *  rather than showing forms the backend will refuse. */
  tabs?: CompanyTabId[];
}

export const CompanyTabs: React.FC<CompanyTabsProps> = ({
  clientId, clientName, initialTab = 'context', urlSync = false, readOnlyContext = false, role,
  tabs,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const offered = tabs
    ? COMPANY_TAB_DEFS.filter(t => tabs.includes(t.id))
    : COMPANY_TAB_DEFS;
  const [active, setActive] = useState<CompanyTabId>(
    () => (urlSync ? normaliseCompanyTab(searchParams.get('tab')) : null) ?? initialTab,
  );

  useEffect(() => {
    if (!urlSync) return;
    const fromUrl = normaliseCompanyTab(searchParams.get('tab'));
    if (fromUrl) setActive(fromUrl);
  }, [urlSync, searchParams]);

  // A narrowed list can drop the open tab (can_manage resolves after the first
  // render), so fall back rather than rendering a panel with no tab selected.
  const shown: CompanyTabId = offered.some(t => t.id === active)
    ? active
    : (offered[0]?.id ?? 'team');

  const pick = useCallback((tab: CompanyTabId) => {
    setActive(tab);
    if (urlSync) setSearchParams({ tab }, { replace: true });
  }, [urlSync, setSearchParams]);

  return (
    <div className="space-y-5">
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex items-end gap-1 overflow-x-auto" aria-label="Company">
          {offered.map(t => {
            const Icon = t.icon;
            const on = shown === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => pick(t.id)}
                className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition-colors ${
                  on
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                <Icon size={16} />
                {t.label}
              </button>
            );
          })}
        </nav>
      </div>

      <CompanyTabPanel
        tab={shown}
        clientId={clientId}
        clientName={clientName}
        readOnlyContext={readOnlyContext}
        role={role}
      />
    </div>
  );
};

export default CompanyTabs;
